import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Form,
  Input,
  Button,
  Tag,
  Image,
  Space,
  Select,
  Modal,
  message,
} from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTable } from '@/hooks/useTable';
import {
  getVoteContestList,
  deleteVoteContest,
  getVoteContestDetail,
  getModelList,
} from '@/api/voteActivity';
import type { VoteContestListParams, VoteContest } from '@/api/voteActivity';
import dayjs from 'dayjs';
import VoteActivityDrawer from './VoteActivityDrawer';
import { contestTypeMapColumn } from './type';

/** 活动状态：0 未开始 1 投稿中 2 用户评审中 3 已结束 4 运营颁奖*/
const STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '未开始', color: 'default' },
  1: { text: '投稿中', color: 'processing' },
  2: { text: '用户评审中', color: 'warning' },
  3: { text: '已结束', color: 'success' },
  4: { text: '运营颁奖', color: 'purple' },
};

const VoteActivityPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<VoteContest>();
  const [searchParams, setSearchParams] = useState<VoteContestListParams>({
    currentPage: 1,
    pageSize: 10,
  });

  const [modelOptions, setModelOptions] = useState<
    { label: string; value: string }[]
  >([]);
  useEffect(() => {
    getModelList().then((res) => {
      if (res.success && Array.isArray(res.data)) {
        const options = res.data.map((item: any) => ({
          label: item.styleType,
          value: item.styleTypeId,
        }));
        setModelOptions(options);
      }
    });
  }, []);

  const {
    data,
    isLoading,
    handleTableChange,
    updateParams,
    currentPage,
    pageSize,
    total,
    refresh,
    setCurrentPage,
    setPageSize,
  } = useTable({
    queryKey: 'vote-contest-list',
    queryFn: async (current, size, params) => {
      const response = await getVoteContestList({
        ...params,
        currentPage: current,
        pageSize: size,
      });
      return response.data;
    },
    defaultParams: searchParams,
  });

  // 从投稿页返回时恢复页码并刷新列表（useQuery 会因 currentPage/pageSize 变化自动请求）
  useEffect(() => {
    const state = location.state as {
      refreshList?: boolean;
      returnPage?: number;
      returnPageSize?: number;
    } | null;
    if (state?.refreshList) {
      if (state.returnPage != null) setCurrentPage(state.returnPage);
      if (state.returnPageSize != null) setPageSize(state.returnPageSize);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const handleSearch = async () => {
    const values = await form.validateFields();
    setSearchParams({ ...searchParams, ...values });
    updateParams({ ...searchParams, ...values });
  };

  const handleReset = () => {
    form.resetFields();
    setSearchParams({
      currentPage: 1,
      pageSize: 10,
    });
    updateParams({
      currentPage: 1,
      pageSize: 10,
    });
  };

  const handleCreate = () => {
    setEditingActivity(undefined);
    setIsDrawerOpen(true);
  };

  const handleEdit = async (record: VoteContest) => {
    const res = await getVoteContestDetail({ id: record.id! });
    if (res.status !== 200) return;
    setEditingActivity(res.data);
    setIsDrawerOpen(true);
  };

  const handleDelete = (record: VoteContest) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除活动"${record.title}"吗？`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await deleteVoteContest(record.id!);
          if (resp.success) {
            message.success('删除成功');
            refresh();
          } else {
            message.error(resp.message || '删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败');
        }
      },
    });
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setEditingActivity(undefined);
  };

  const handleDrawerSuccess = () => {
    setIsDrawerOpen(false);
    setEditingActivity(undefined);
    refresh();
  };

  const handleViewSubmissions = (id: number, status: number) => {
    navigate(`/vote-activity/submissions/${id}?status=${status}`, {
      state: { fromListPage: currentPage, fromListPageSize: pageSize },
    });
  };

  const columns = [
    {
      title: 'ContestId',
      dataIndex: 'id',
    },
    {
      title: '活动图片',
      dataIndex: 'contestImage',
      key: 'contestImage',
      width: 120,
      render: (url: string) => (
        <Image
          src={url}
          alt="活动图片"
          style={{ width: 80, height: 80, objectFit: 'cover' }}
        />
      ),
    },
    {
      title: '活动标题',
      key: 'title',
      width: 300,
      render: (_, record: VoteContest) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.title}</div>
          <div style={{ color: '#666' }}>{record.subtitle}</div>
        </div>
      ),
    },
    {
      title: '投稿时间',
      key: 'submitTime',
      width: 250,
      render: (record: VoteContest) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <div>
            开始：{dayjs(record.submitBeginTime).format('YYYY-MM-DD HH:mm')}
          </div>
          <div>
            结束：{dayjs(record.submitEndTime).format('YYYY-MM-DD HH:mm')}
          </div>
        </div>
      ),
    },
    {
      title: '奖励设置',
      key: 'prize',
      width: 180,
      render: (_, record: VoteContest) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <div>获奖人数：{record.prizeNum}</div>
          <div>每人奖励：{record.prizeQuota}</div>
        </div>
      ),
    },
    {
      title: '活动类型',
      key: 'contestType',
      dataIndex: 'contestType',
      width: 160,
      render: (_: number) => {
        if (!_) return "-";
        return contestTypeMapColumn.get(_)
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: number) => (
        <Tag color={STATUS_MAP[status]?.color}>{STATUS_MAP[status]?.text ?? '-'}</Tag>
      ),
    },
    {
      title: '创建人',
      dataIndex: 'createUserName',
      key: 'createUserName',
      width: 100,
      render: (value: string) => value || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record: VoteContest) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleViewSubmissions(record.id!, record.status ?? 0)}
          >
            查看投稿
          </Button>
          {(record.status === 0 || record.status === 1) && (
            <Button type="link" onClick={() => handleEdit(record)}>
              编辑
            </Button>
          )}
          <Button type="link" danger onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="投票活动"
      extra={
        <Button type="primary" onClick={handleCreate}>
          创建活动
        </Button>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item name="id" label="活动ID">
            <Input placeholder="请输入活动ID" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="title" label="活动标题">
            <Input placeholder="请输入活动标题" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="userName" label="创建人">
            <Input placeholder="请输入创建人" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="status" label="活动状态">
            <Select
              style={{ width: 160 }}
              placeholder="请选择状态"
              allowClear
              options={Object.entries(STATUS_MAP).map(([value, { text }]) => ({
                label: text,
                value: Number(value),
              }))}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
        loading={isLoading}
        onChange={handleTableChange}
        scroll={{ x: 1300 }}
      />

      {isDrawerOpen && (
        <VoteActivityDrawer
          visible={isDrawerOpen}
          editData={editingActivity}
          onSuccess={handleDrawerSuccess}
          onClose={handleDrawerClose}
          modelOptions={modelOptions}
        />
      )}
    </Card>
  );
};

export default VoteActivityPage;
